import { countries } from "countries-list";

/** Set of valid ISO 2-letter country codes (for when API sends code instead of name) */
const VALID_COUNTRY_CODES = new Set(Object.keys(countries));

/** Map country name -> ISO 3166-1 alpha-2 code (e.g. "United Arab Emirates" -> "AE") */
const countryNameToCode: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const [code, data] of Object.entries(countries)) {
    map[data.name] = code;
    map[data.name.toLowerCase()] = code;
    map[data.name.toUpperCase()] = code;
  }
  return map;
})();

/** Common aliases / alternate names that API might send instead of full country name */
const COUNTRY_ALIASES: Record<string, string> = {
  "uk": "GB",
  "uae": "AE",
  "usa": "US",
  "u.s.a.": "US",
  "u.s.a": "US",
  "united states": "US",
  "united states of america": "US",
  "united kingdom": "GB",
  "great britain": "GB",
  "england": "GB",
  "korea": "KR",
  "south korea": "KR",
  "north korea": "KP",
  "tanzania": "TZ",
  "united republic of tanzania": "TZ",
  "vietnam": "VN",
  "viet nam": "VN",
  "russia": "RU",
  "russian federation": "RU",
  "iran": "IR",
  "iran (islamic republic of)": "IR",
  "syria": "SY",
  "syrian arab republic": "SY",
  "palestine": "PS",
  "state of palestine": "PS",
  "bolivia": "BO",
  "bolivia (plurinational state of)": "BO",
  "venezuela": "VE",
  "venezuela (bolivarian republic of)": "VE",
  "taiwan": "TW",
  "taiwan, province of china": "TW",
  "moldova": "MD",
  "republic of moldova": "MD",
};

const FLAG_CDN_BASE = "https://flagcdn.com";
const FLAG_SIZE = "24x18";

/** English cities: when country is UK and city is in this set, show England flag (gb-eng) instead of UK. Also used when country is missing to show flag for same city. */
const ENGLAND_CITIES = new Set(
  [
    "London", "Birmingham", "Manchester", "Leeds", "Liverpool", "Bristol", "Sheffield",
    "Newcastle", "Newcastle upon Tyne", "Nottingham", "Leicester", "Southampton", "Brighton",
    "Plymouth", "Wolverhampton", "Reading", "Derby", "Stoke-on-Trent", "York", "Northampton",
    "Luton", "Oxford", "Cambridge", "Portsmouth", "Milton Keynes", "Ipswich", "Norwich",
    "Exeter", "Cheltenham", "Gloucester", "Bath", "Bournemouth", "Swindon", "Peterborough",
    "Lincoln", "Canterbury", "Colchester", "Southend", "Maidstone", "Hull", "Kingston upon Hull",
  ].map((c) => c.toLowerCase().trim())
);

function isCityInEngland(city: string | undefined | null): boolean {
  const c = (city ?? "").trim().toLowerCase();
  return c.length > 0 && ENGLAND_CITIES.has(c);
}

/**
 * Returns ISO 2-letter country code from country name (or code), or null if not found.
 * Tries: exact name, lowercase name, aliases, then 2-letter code if API sent code.
 */
export function getCountryCode(countryName: string | undefined | null): string | null {
  const name = (countryName ?? "").trim();
  if (!name) return null;
  const twoLetter = name.length === 2 ? name.toUpperCase() : null;
  if (twoLetter && VALID_COUNTRY_CODES.has(twoLetter)) return twoLetter;
  const code = countryNameToCode[name] ?? countryNameToCode[name.toLowerCase()] ?? COUNTRY_ALIASES[name.toLowerCase()];
  return code ?? null;
}

/**
 * Returns flag image URL from flagcdn.com CDN for the given country name.
 * Optional city: when country is United Kingdom and city is in England (e.g. London),
 * returns England flag (gb-eng) instead of UK flag.
 * When country is empty but city is known (e.g. Birmingham), returns flag for that city's region so same city always shows same flag.
 */
export function getCountryFlagUrl(
  countryName: string | undefined | null,
  city?: string | undefined | null
): string | null {
  const country = (countryName ?? "").trim();
  const cityTrimmed = (city ?? "").trim();

  if (country) {
    const code = getCountryCode(country);
    if (!code) return null;
    if (code === "GB" && isCityInEngland(city)) {
      return `${FLAG_CDN_BASE}/${FLAG_SIZE}/gb-eng.png`;
    }
    return `${FLAG_CDN_BASE}/${FLAG_SIZE}/${code.toLowerCase()}.png`;
  }

  // Country missing: infer flag from known city so same city always shows flag (e.g. Birmingham -> England)
  if (cityTrimmed && isCityInEngland(city)) {
    return `${FLAG_CDN_BASE}/${FLAG_SIZE}/gb-eng.png`;
  }
  return null;
}
